import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import WaitingRoom from '../components/WaitingRoom';
import MultiplayerGame from '../components/MultiplayerGame';
import { useMatchmaking } from '../hooks/useMatchmaking';
import { useAuth } from '../contexts/AuthContext';
import { Question } from '../types';
import questionsData from '../data/questions-all-1000.json';
import {
  requestRematch,
  acceptRematch,
  declineRematch,
  listenToMatch
} from '../services/matchmaking.service';

const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
    padding-top: ${({ theme }) => theme.spacing.md};
  }
`;

const Header = styled.div`
  max-width: 1200px;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  @media (max-width: 767px) {
    font-size: 1.5rem;
  }
`;

const BackButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: 1rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 767px) {
    font-size: 0.9rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  }
`;

const ResultsContainer = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  text-align: center;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }
`;

const ResultTitle = styled.h2`
  font-size: 1.75rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    font-size: 1.5rem;
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const ScoresGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  margin: ${({ theme }) => theme.spacing.lg} 0;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
    margin: ${({ theme }) => theme.spacing.md} 0;
  }
`;

const PlayerResult = styled.div<{ $isWinner: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme, $isWinner }) =>
    $isWinner ? theme.colors.success + '20' : theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 3px solid ${({ theme, $isWinner }) =>
    $isWinner ? theme.colors.success : theme.colors.surface};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
    border: 2px solid ${({ theme, $isWinner }) =>
      $isWinner ? theme.colors.success : theme.colors.surface};
  }
`;

const PlayerResultName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  @media (max-width: 767px) {
    font-size: 1rem;
  }
`;

const PlayerResultScore = styled.div`
  font-size: 2.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};

  @media (max-width: 767px) {
    font-size: 2rem;
  }
`;

const WinnerBadge = styled.div`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.success};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 600;
  font-size: 0.9rem;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const VersusText = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};

  @media (max-width: 767px) {
    display: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: center;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 767px) {
    margin-top: ${({ theme }) => theme.spacing.md};
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const Button = styled(motion.button)<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  background: ${({ theme, $variant }) =>
    $variant === 'secondary' ? theme.colors.surface : theme.colors.primary};
  color: ${({ theme, $variant }) =>
    $variant === 'secondary' ? theme.colors.text : '#fff'};
  box-shadow: ${({ theme }) => theme.shadows.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 767px) {
    font-size: 0.85rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  }
`;

const ErrorMessage = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.error + '20'};
  border: 2px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
  font-weight: 600;
`;

const ResultSubtitle = styled(motion.p)`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing.sm} 0;
  line-height: 1.5;

  @media (max-width: 767px) {
    font-size: 0.85rem;
    margin: ${({ theme }) => theme.spacing.xs} 0;
  }
`;

const XPGain = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary}20;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  margin: ${({ theme }) => theme.spacing.md} 0;

  @media (max-width: 767px) {
    font-size: 0.9rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    margin: ${({ theme }) => theme.spacing.sm} 0;
  }
`;

const MultiplayerScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser: user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [rematchReceived, setRematchReceived] = useState(false);
  const [processingRematch, setProcessingRematch] = useState(false);

  // Select 10 random questions for the match
  useEffect(() => {
    const allQuestions = questionsData as Question[];
    const shuffled = [...allQuestions].sort(() => Math.random() - 0.5);
    setQuestions(shuffled.slice(0, 10));
  }, []);

  const {
    state,
    match,
    error,
    isPlayer1,
    opponentName,
    joinQueue,
    leaveQueue,
    markReady,
    submitPlayerAnswer,
    forfeit,
  } = useMatchmaking(questions);

  // Auto-join queue when questions are ready
  useEffect(() => {
    if (questions.length > 0 && state === 'idle') {
      joinQueue();
    }
  }, [questions, state, joinQueue]);

  // Listen for rematch requests on completed matches
  useEffect(() => {
    if (state === 'completed' && match?.id && user) {
      const unsubscribe = listenToMatch(match.id, (updatedMatch) => {
        // Check if opponent requested a rematch
        if (updatedMatch.rematchRequestedBy && updatedMatch.rematchRequestedBy !== user.uid) {
          setRematchReceived(true);
        }
        // Check if rematch was accepted and new match created
        if (updatedMatch.rematchAccepted && updatedMatch.rematchMatchId) {
          // Navigate to the new match (this would require updating useMatchmaking to handle existing match ID)
          // For now, just reset and join queue
          window.location.reload();
        }
      });

      return () => unsubscribe();
    }
  }, [state, match?.id, user]);

  const handleBackToMenu = () => {
    if (state === 'searching' || state === 'waiting') {
      leaveQueue();
    }
    navigate('/mode-select');
  };

  const handlePlayAgain = () => {
    // Reload questions
    const allQuestions = questionsData as Question[];
    const shuffled = [...allQuestions].sort(() => Math.random() - 0.5);
    setQuestions(shuffled.slice(0, 10));

    // Join queue again
    setTimeout(() => {
      joinQueue();
    }, 500);
  };

  const handleRequestRematch = async () => {
    if (!match?.id || !user) return;

    try {
      setProcessingRematch(true);
      await requestRematch(match.id, user.uid);
      setRematchRequested(true);
    } catch (err) {
      console.error('Failed to request rematch:', err);
    } finally {
      setProcessingRematch(false);
    }
  };

  const handleAcceptRematch = async () => {
    if (!match?.id || !user) return;

    try {
      setProcessingRematch(true);
      // Generate new questions for rematch
      const allQuestions = questionsData as Question[];
      const shuffled = [...allQuestions].sort(() => Math.random() - 0.5);
      const newQuestions = shuffled.slice(0, 10);

      await acceptRematch(match.id, user.uid, newQuestions);
      // The listener will handle navigation when rematch is created
    } catch (err) {
      console.error('Failed to accept rematch:', err);
      setProcessingRematch(false);
    }
  };

  const handleDeclineRematch = async () => {
    if (!match?.id) return;

    try {
      await declineRematch(match.id);
      setRematchReceived(false);
    } catch (err) {
      console.error('Failed to decline rematch:', err);
    }
  };

  const handleViewHistory = () => {
    navigate('/match-history');
  };

  // Trigger confetti when match completes with victory
  useEffect(() => {
    if (state === 'completed' && match && match.winnerId === user?.uid) {
      // Victory confetti!
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      const randomInRange = (min: number, max: number) => {
        return Math.random() * (max - min) + min;
      };

      const interval: NodeJS.Timeout = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50 * (timeLeft / duration);

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [state, match, user]);

  if (!user) {
    return (
      <Container>
        <ErrorMessage>
          You must be logged in to play multiplayer.
        </ErrorMessage>
        <ButtonGroup>
          <Button onClick={() => navigate('/login')}>Login</Button>
        </ButtonGroup>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>Multiplayer</Title>
          <BackButton onClick={handleBackToMenu} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            Back to Menu
          </BackButton>
        </Header>
        <ErrorMessage>{error}</ErrorMessage>
        <ButtonGroup>
          <Button onClick={handlePlayAgain}>Try Again</Button>
          <Button $variant="secondary" onClick={handleBackToMenu}>
            Back to Menu
          </Button>
        </ButtonGroup>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Multiplayer Battle</Title>
        {(state === 'searching' || state === 'found' || state === 'waiting') && (
          <BackButton onClick={handleBackToMenu} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            Cancel
          </BackButton>
        )}
        {state === 'completed' && (
          <BackButton onClick={handleBackToMenu} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            Back to Menu
          </BackButton>
        )}
      </Header>

      <AnimatePresence mode="wait">
        {/* Searching/Waiting/Found States */}
        {(state === 'searching' || state === 'found' || state === 'waiting') && (
          <WaitingRoom
            status={state}
            opponentName={match?.player2?.displayName || opponentName}
            onReady={markReady}
            onCancel={leaveQueue}
            isReady={match ? (isPlayer1 ? match.player1.ready : match.player2?.ready) : false}
            opponentReady={match ? (isPlayer1 ? match.player2?.ready : match.player1.ready) : false}
          />
        )}

        {/* Playing State */}
        {state === 'playing' && match && (
          <MultiplayerGame
            match={match}
            isPlayer1={isPlayer1}
            onAnswerSubmit={submitPlayerAnswer}
            onForfeit={forfeit}
          />
        )}

        {/* Completed State */}
        {state === 'completed' && match && (
          <ResultsContainer
            key="results"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4 }}
          >
            {match.winnerId === null ? (
              <>
                <ResultTitle>It's a Draw! 🤝</ResultTitle>
                <ResultSubtitle
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  Both players scored {match.player1.score} points! Great minds think alike.
                </ResultSubtitle>
                <XPGain
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                >
                  ⭐ +{50 + Math.floor(match.player1.score / 20)} XP
                </XPGain>
              </>
            ) : match.winnerId === user.uid ? (
              <>
                <ResultTitle>Victory! 🎉</ResultTitle>
                <ResultSubtitle
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  Outstanding! You've proven your knowledge of Scripture.
                  {match.player1.score > match.player2!.score + 500 && " A dominant performance!"}
                </ResultSubtitle>
                <XPGain
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                >
                  ⭐ +{100 + Math.floor((isPlayer1 ? match.player1.score : match.player2!.score) / 10)} XP
                </XPGain>
              </>
            ) : (
              <>
                <ResultTitle>Defeat 😔</ResultTitle>
                <ResultSubtitle
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  Don't be discouraged! "Consider it pure joy... because you know that the testing of your faith produces perseverance." - James 1:2-3
                </ResultSubtitle>
                <XPGain
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                >
                  ⭐ +{25 + Math.floor((isPlayer1 ? match.player1.score : match.player2!.score) / 20)} XP
                </XPGain>
              </>
            )}

            <ScoresGrid>
              <PlayerResult $isWinner={match.winnerId === match.player1.uid}>
                <PlayerResultName>{isPlayer1 ? 'You' : match.player1.displayName}</PlayerResultName>
                <PlayerResultScore>{match.player1.score}</PlayerResultScore>
                {match.winnerId === match.player1.uid && <WinnerBadge>Winner!</WinnerBadge>}
              </PlayerResult>

              <VersusText>VS</VersusText>

              <PlayerResult $isWinner={match.winnerId === match.player2?.uid}>
                <PlayerResultName>{isPlayer1 ? match.player2?.displayName : 'You'}</PlayerResultName>
                <PlayerResultScore>{match.player2?.score || 0}</PlayerResultScore>
                {match.winnerId === match.player2?.uid && <WinnerBadge>Winner!</WinnerBadge>}
              </PlayerResult>
            </ScoresGrid>

            <ButtonGroup>
              {rematchReceived ? (
                <>
                  <Button onClick={handleAcceptRematch} disabled={processingRematch} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    {processingRematch ? 'Processing...' : 'Accept Rematch'}
                  </Button>
                  <Button $variant="secondary" onClick={handleDeclineRematch} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    Decline
                  </Button>
                </>
              ) : rematchRequested ? (
                <Button disabled whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  Rematch Requested...
                </Button>
              ) : (
                <Button onClick={handleRequestRematch} disabled={processingRematch} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  Request Rematch
                </Button>
              )}
              <Button onClick={handlePlayAgain} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                Find New Opponent
              </Button>
              <Button $variant="secondary" onClick={handleViewHistory} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                Match History
              </Button>
              <Button $variant="secondary" onClick={handleBackToMenu} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                Back to Menu
              </Button>
            </ButtonGroup>
          </ResultsContainer>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default MultiplayerScreen;
